import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading } from '../components/ui';
import { useApi, useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface ServicesContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
  cta_text_en: string;
  cta_text_ro: string;
}

interface Service {
  id: number;
  icon: string;
  title_en: string;
  title_ro: string;
  description_en: string;
  description_ro: string;
  link_url?: string;
  sort_order: number;
}

const ServicesPage: React.FC = () => {
  const { data, loading, fetch, update } = useApi<ServicesContent>('/api/content/services');
  const services = useCrud<Service>('/api/services-list');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<ServicesContent>({
    title_en: '', title_ro: '',
    subtitle_en: '', subtitle_ro: '',
    cta_text_en: '', cta_text_ro: '',
  });
  const [saving, setSaving] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);

  useEffect(() => { fetch(); services.fetchAll(); }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        title_en: data.title_en || '',
        title_ro: data.title_ro || '',
        subtitle_en: data.subtitle_en || '',
        subtitle_ro: data.subtitle_ro || '',
        cta_text_en: data.cta_text_en || '',
        cta_text_ro: data.cta_text_ro || '',
      });
    }
  }, [data]);

  const handleChange = (field: keyof ServicesContent) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('Services section saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  const handleServiceSave = async (service: Partial<Service>) => {
    if (editingService?.id) {
      const result = await services.update(editingService.id, service);
      if (result) {
        showToast('Service updated!', 'success');
        setEditingService(null);
      }
    } else {
      const result = await services.create(service);
      if (result) {
        showToast('Service created!', 'success');
        setEditingService(null);
      }
    }
  };

  const handleServiceDelete = async (id: number) => {
    if (window.confirm('Delete this service?')) {
      const result = await services.remove(id);
      if (result) showToast('Service deleted', 'success');
    }
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Services Section"
        subtitle="Manage your services header and individual service cards"
        actions={<Button onClick={handleSave} loading={saving}>Save Changes</Button>}
      />

      <div className="admin-content">
        <Card title="Section Header">
          <LanguageTabs currentLang={lang} onChange={setLang} />
          <div className="admin-form">
            {lang === 'en' ? (
              <>
                <Input label="Title (EN)" value={form.title_en} onChange={handleChange('title_en')} />
                <Input label="Subtitle (EN)" value={form.subtitle_en} onChange={handleChange('subtitle_en')} />
                <Input label="CTA Button Text (EN)" value={form.cta_text_en} onChange={handleChange('cta_text_en')} />
              </>
            ) : (
              <>
                <Input label="Title (RO)" value={form.title_ro} onChange={handleChange('title_ro')} />
                <Input label="Subtitle (RO)" value={form.subtitle_ro} onChange={handleChange('subtitle_ro')} />
                <Input label="CTA Button Text (RO)" value={form.cta_text_ro} onChange={handleChange('cta_text_ro')} />
              </>
            )}
          </div>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card
            title="Service Cards"
            actions={
              <Button size="sm" onClick={() => setEditingService({ id: 0, icon: '🎵', title_en: '', title_ro: '', description_en: '', description_ro: '', sort_order: services.items.length })}>
                + Add Service
              </Button>
            }
          >
            <div className="admin-list-editor">
              {services.items.map(service => (
                <div key={service.id} className="admin-list-item">
                  <span style={{ fontSize: '1.5rem' }}>{service.icon}</span>
                  <div className="admin-list-item-content">
                    <div>
                      <strong>{service.title_en}</strong>
                      <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>{service.description_en.substring(0, 50)}...</div>
                    </div>
                  </div>
                  <div className="admin-list-item-actions">
                    <Button size="sm" variant="secondary" onClick={() => setEditingService(service)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => handleServiceDelete(service.id)}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Edit Modal */}
        {editingService && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingService(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingService.id ? 'Edit Service' : 'Add Service'}</h3>
              <ServiceForm
                service={editingService}
                onSave={handleServiceSave}
                onCancel={() => setEditingService(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const ServiceForm: React.FC<{
  service: Service;
  onSave: (s: Partial<Service>) => void;
  onCancel: () => void;
}> = ({ service, onSave, onCancel }) => {
  const [form, setForm] = useState(service);
  const [lang, setLang] = useState<'en' | 'ro'>('en');

  return (
    <div className="admin-form">
      <Input label="Icon (emoji)" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} />
      <LanguageTabs currentLang={lang} onChange={setLang} />
      {lang === 'en' ? (
        <>
          <Input label="Title (EN)" value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} />
          <Input label="Description (EN)" value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))} />
        </>
      ) : (
        <>
          <Input label="Title (RO)" value={form.title_ro} onChange={e => setForm(f => ({ ...f, title_ro: e.target.value }))} />
          <Input label="Description (RO)" value={form.description_ro} onChange={e => setForm(f => ({ ...f, description_ro: e.target.value }))} />
        </>
      )}
      <Input label="Link URL (optional)" value={form.link_url || ''} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="/beats" />
      <Input label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default ServicesPage;
